// PaymentTermsGate — the terms, the authorization to charge, and the single
// acceptance control that gates order submission (design §4.4, §4.5, §3.8, D4).
//
// ONE TICK, SEVERAL DOCUMENTS. D6 describes a single accept action, and §4.4
// records acceptance as a SET rather than one column, because D4 names TWO
// documents ("payment terms AND terms of use") and an order can carry one-time
// and recurring characteristics together. So the documents are presented
// together above ONE checkbox, and every document presented is echoed on
// submit. The checkbox label names what is being accepted — an unlabelled tick
// is not an acceptance of anything in particular.
//
// A VERSION REFERENCE, NEVER A BOOLEAN (§4.1). What leaves this component is
// the id of each revision as displayed. The revision rows are immutable and
// append-only, so joining an order to its accepted revisions returns the exact
// bytes shown, years later, however many times the live text has since changed.
// A boolean could not have met that requirement, and neither could a pointer to
// whatever text happens to be live at read time.
//
// THE COPY IS THE OPERATOR'S, NOT OURS (D5). Every word of legal text rendered
// here comes from the fetched documents. This file contributes chrome — a
// heading, a fallback label, an error message — and contributes no term,
// no authorization language, and nothing a customer could be held to.
//
// FAIL CLOSED (§4.5). No documents, an empty set, or a failed fetch all render
// a blocking message rather than an unticked box over silence. The client-side
// disable is convenience; the server re-checks acceptance and rejects an order
// without it.

import { useEmbedTokens, EmbedBanner, EmbedCheckbox, EmbedMarkdown } from "../../embed-shared/primitives";
import { termsDocumentTitle, type PaymentTermsDocumentWire } from "./payment-terms-api";

export interface PaymentTermsGateProps {
  /** Active revisions to display. `null` means "still loading". */
  documents: PaymentTermsDocumentWire[] | null;
  /** Message when the fetch failed. Blocks acceptance while set. */
  loadError: string | null;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  /** Shown when the customer tried to submit without ticking. */
  acceptanceError?: string | null;
  /** Id prefix so a second gate on one page cannot collide. */
  idPrefix?: string;
}

/** The id of the acceptance checkbox, exported so callers can focus it. */
export function termsAcceptanceInputId(idPrefix = "pay-terms"): string {
  return `${idPrefix}-accept`;
}

/**
 * Whether the gate can be satisfied at all. Blocking states are the same set
 * the gate renders a message for, kept in one predicate so a caller cannot
 * disagree with what the customer is looking at.
 */
export function termsAreAvailable(
  documents: PaymentTermsDocumentWire[] | null,
  loadError: string | null,
): boolean {
  return loadError === null && documents !== null && documents.length > 0;
}

export function PaymentTermsGate({
  documents,
  loadError,
  accepted,
  onAcceptedChange,
  acceptanceError,
  idPrefix = "pay-terms",
}: PaymentTermsGateProps) {
  const t = useEmbedTokens();
  const acceptId = termsAcceptanceInputId(idPrefix);
  const errorId = `${acceptId}-err`;

  if (loadError) {
    return (
      <section aria-label="Payment terms" style={{ marginTop: t.spacing.lg }}>
        <EmbedBanner tone="error">{loadError}</EmbedBanner>
      </section>
    );
  }

  if (documents === null) {
    return (
      <section aria-label="Payment terms" style={{ marginTop: t.spacing.lg }}>
        <p style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, margin: 0 }}>
          Loading payment terms…
        </p>
      </section>
    );
  }

  if (documents.length === 0) {
    // An empty set is NOT "no terms needed" — it is a platform whose legal text
    // is unconfigured, and an order accepted here would record an acceptance of
    // nothing at all.
    return (
      <section aria-label="Payment terms" style={{ marginTop: t.spacing.lg }}>
        <EmbedBanner tone="error">
          Payment terms are not available right now, so this order cannot be placed. Please try again
          shortly.
        </EmbedBanner>
      </section>
    );
  }

  // Falls back to naming the documents shown. Operator-configured text wins
  // when supplied (D5); this fallback states what the tick refers to without
  // asserting anything the operator has not written.
  const configuredLabel = documents.find(
    (d) => d.acceptance_label && d.acceptance_label.trim().length > 0,
  )?.acceptance_label;
  const acceptanceLabel =
    configuredLabel?.trim() ??
    `I have read and accept the ${documents.map(termsDocumentTitle).join(" and ")} above.`;

  return (
    <section aria-label="Payment terms" style={{ marginTop: t.spacing.lg }}>
      {documents.map((doc) => (
        <article
          key={doc.id}
          style={{
            border: `1px solid ${t.colors.borderSubtle}`,
            borderRadius: t.borderRadius,
            padding: t.spacing.md,
            marginBottom: t.spacing.md,
            background: t.colors.surface,
          }}
        >
          <h4
            style={{
              fontSize: t.fontSize.sm,
              fontWeight: 600,
              color: t.colors.text,
              margin: `0 0 ${t.spacing.sm}px 0`,
            }}
          >
            {termsDocumentTitle(doc)}
          </h4>
          {/* Scrolls within itself rather than pushing the acceptance control
              off-screen — the tick must stay reachable without hunting. */}
          <div style={{ maxHeight: 220, overflowY: "auto", fontSize: t.fontSize.sm, color: t.colors.text }}>
            <EmbedMarkdown source={doc.body} />
          </div>
        </article>
      ))}

      <EmbedCheckbox
        id={acceptId}
        label={acceptanceLabel}
        checked={accepted}
        onChange={(e) => onAcceptedChange(e.target.checked)}
        invalid={!!acceptanceError}
        aria-describedby={acceptanceError ? errorId : undefined}
      />

      {/* Mounted unconditionally (TD-290 (d) pattern): a live region that
          appears together with its content is routinely missed by screen
          readers, so it exists empty and fills in. */}
      <div aria-live="assertive" style={{ marginTop: acceptanceError ? t.spacing.sm : 0 }}>
        {acceptanceError && (
          <span
            id={errorId}
            role="alert"
            style={{ fontSize: t.fontSize.sm, color: t.colors.error }}
          >
            {acceptanceError}
          </span>
        )}
      </div>
    </section>
  );
}
